import {
    AlertCircle,
    ArrowLeft,
    BookOpen,
    ChevronRight,
    Clock,
    Info,
    List,
    Lock,
    Pencil,
    PauseCircle,
    Receipt,
    Trash2,
    X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCustomerPackageById } from '../../../api/package.api';
import styles from '../../../assets/scss/pages/CustomerPackages.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { usePermissions } from '../../../hooks/usePermissions';
import CustomerPackageUsages from './CustomerPackageUsages';

// Port từ app: screens/admin/CustomerPackageDetailModal.js.
// 2 chế độ: 'detail' (chi tiết thẻ) | 'guide' (hướng dẫn sử dụng).
// Adjust web: "Lịch dùng gói" (booking) chờ Bước 2 (Lịch hẹn) nên tạm ẩn;
// "Lịch sử dùng" mở modal CustomerPackageUsages stack lên trên.

const STATUS_META = {
    active: { label: 'Đang dùng', color: '#16a34a', bg: '#dcfce7' },
    frozen: { label: 'Bảo lưu', color: '#0891b2', bg: '#cffafe' },
    completed: { label: 'Hoàn tất', color: '#64748b', bg: '#f1f5f9' },
    expired: { label: 'Hết hạn', color: '#dc2626', bg: '#fee2e2' },
    refunded: { label: 'Đã hoàn', color: '#9333ea', bg: '#f3e8ff' },
    cancelled: { label: 'Đã huỷ', color: '#6b7280', bg: '#f3f4f6' },
};

const formatVnd = (n) => `${Number(n || 0).toLocaleString('vi-VN')}đ`;

const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

const formatDateTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())} ${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
};

export default function CustomerPackageDetailModal({ open, packageId, onClose }) {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canViewBillDetail = hasPermission(PERMISSIONS.BILL.VIEW_DETAIL);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [mode, setMode] = useState('detail'); // 'detail' | 'guide'
    const [usagesOpen, setUsagesOpen] = useState(false);

    useEffect(() => {
        if (!open || !packageId) return;
        setMode('detail');
        let cancelled = false;
        setLoading(true);
        setError('');
        getCustomerPackageById(packageId)
            .then((res) => {
                if (!cancelled) setData(res?.data?.data || null);
            })
            .catch((err) => {
                if (!cancelled) setError(err?.response?.data?.message || err.message || 'Lỗi tải');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, packageId]);

    if (!open) return null;

    const packageDisplayName = data?.templateSnapshot?.name || data?.package?.name || '';

    const handleViewBill = () => {
        if (!data?.billId) return;
        onClose();
        navigate(`/admin/bills/${data.billId}`);
    };

    const handleRequestDelete = () => {
        // Port app: không xoá thẻ trực tiếp, phải xoá hoá đơn mua thẻ.
        const ok = window.confirm(
            'Không thể xoá thẻ dịch vụ trực tiếp.\nĐể xoá thẻ, vui lòng xoá hoá đơn mua thẻ.\n\nXem hoá đơn ngay?',
        );
        if (ok) handleViewBill();
    };

    return (
        <>
            <div className={styles.modalOverlay} onClick={onClose}>
                <div className={styles.sheet} onClick={(e) => e.stopPropagation()}>
                    {loading ? (
                        <div className={styles.sheetCenter}>
                            <div className={styles.spinner} />
                        </div>
                    ) : error ? (
                        <div className={styles.sheetCenter}>
                            <AlertCircle size={40} color="#ef4444" />
                            <span className={styles.muted} style={{ color: '#ef4444' }}>{error}</span>
                        </div>
                    ) : mode === 'guide' ? (
                        <>
                            <div className={styles.sheetBody}>
                                <GuideContent data={data} onBack={() => setMode('detail')} onClose={onClose} />
                            </div>
                            <div className={styles.actionRow}>
                                <button className={styles.backBtn} onClick={() => setMode('detail')}>
                                    <ArrowLeft size={20} /> Quay lại
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={styles.sheetBody}>
                                <DetailContent
                                    data={data}
                                    onClose={onClose}
                                    onShowGuide={() => setMode('guide')}
                                />
                            </div>
                            <div className={styles.actionRow}>
                                {canViewBillDetail ? (
                                    <ActionBtn
                                        icon={<Receipt size={22} />}
                                        label="Hoá đơn bán"
                                        onClick={handleViewBill}
                                        color="#0089F6"
                                    />
                                ) : null}
                                <ActionBtn
                                    icon={<Clock size={22} />}
                                    label="Lịch sử dùng"
                                    onClick={() => setUsagesOpen(true)}
                                    color="#16a34a"
                                />
                                <ActionBtn
                                    icon={<Trash2 size={22} />}
                                    label="Xoá thẻ"
                                    onClick={handleRequestDelete}
                                    color="#dc3545"
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>

            <CustomerPackageUsages
                open={usagesOpen}
                packageId={packageId}
                packageName={packageDisplayName}
                onClose={() => setUsagesOpen(false)}
            />
        </>
    );
}

function DetailContent({ data, onClose, onShowGuide }) {
    if (!data) return null;
    const snap = data.templateSnapshot || {};
    const sellerName = data.sellerEmployee?.name || '—';
    const statusMeta = STATUS_META[data.status] || STATUS_META.active;
    const usedSessions = Number(data.usedSessions || 0);
    const total = Number(data.totalSessions || 0);
    const bonus = Number(data.bonusSessions || 0);
    const remaining = Math.max(0, total + bonus - usedSessions);
    const items = Array.isArray(snap.items) ? snap.items : [];

    return (
        <>
            <div className={styles.headerRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 className={styles.title}>{snap.name || data.package?.name || 'Thẻ dịch vụ'}</h3>
                    <span className={styles.code}>Mã thẻ: {data.code}</span>
                </div>
                <span
                    className={styles.statusBadge}
                    style={{ background: statusMeta.bg, color: statusMeta.color }}
                >
                    {statusMeta.label}
                </span>
                <button className={styles.closeBtn} onClick={onClose}>
                    <X size={22} />
                </button>
            </div>

            <div className={styles.statsRow}>
                <Stat label="Đã dùng" value={`${usedSessions}/${total}${bonus > 0 ? ` +${bonus}` : ''}`} />
                <Stat label="Còn lại" value={`${remaining} buổi`} highlight />
                <Stat label="HSD" value={data.expiredAt ? formatDate(data.expiredAt) : 'Vô hạn'} />
            </div>

            <Section title="Thông tin tài chính">
                <Row label="Giá thực bán" value={formatVnd(data.totalContractValue)} valueColor="#f45a31" bold />
                <Row label="Giá / buổi" value={formatVnd(data.amountPerSession)} />
                <Row label="Phương thức" value={data.paymentMode === 'upfront' ? 'Trả đủ trước' : 'Trả từng buổi'} />
                <Row label="Hoa hồng NV bán" value={formatVnd(data.sellerCommission)} />
                <Row
                    label="% hoa hồng"
                    value={`Bán ${Number(snap.sellerCommissionPercent || 0)}% • CS ${Number(snap.performerCommissionPercent || 0)}%`}
                />
                <Row label="NV bán" value={sellerName} />
                <button className={styles.guideBtn} onClick={onShowGuide}>
                    <BookOpen size={18} />
                    <span>Xem hướng dẫn sử dụng</span>
                    <ChevronRight size={18} />
                </button>
            </Section>

            {data.isOverridden && (
                <Section title="Đã chỉnh từ template" titleIcon={<Pencil size={14} />} titleColor="#f59e0b">
                    {data.overrideReason ? (
                        <div className={styles.detailNoteBox}>
                            <span className={styles.detailNoteText}>{data.overrideReason}</span>
                        </div>
                    ) : (
                        <span className={styles.muted}>Không có lý do</span>
                    )}
                </Section>
            )}

            {items.length > 0 && (
                <Section title="Dịch vụ trong gói">
                    {items.map((it, idx) => (
                        <div key={idx} className={styles.itemRow}>
                            <span className={styles.bullet} />
                            <span className={styles.itemName}>
                                {it.name}
                                {it.quantity > 1 ? ` ×${it.quantity}/buổi` : ''}
                                {it.isVirtual ? ' (Tặng kèm)' : ''}
                            </span>
                        </div>
                    ))}
                </Section>
            )}

            {data.status === 'frozen' && data.frozenAt && (
                <Section title="Bảo lưu" titleIcon={<PauseCircle size={14} />} titleColor="#0891b2">
                    <Row label="Bắt đầu bảo lưu" value={formatDateTime(data.frozenAt)} />
                    <Row label="Tổng ngày bảo lưu" value={`${data.frozenDays || 0} ngày`} />
                    {data.frozenReason && (
                        <div className={styles.detailNoteBox}>
                            <span className={styles.detailNoteText}>{data.frozenReason}</span>
                        </div>
                    )}
                </Section>
            )}

            {data.note ? (
                <Section title="Ghi chú khách">
                    <div className={styles.detailNoteBox}>
                        <span className={styles.detailNoteText}>{data.note}</span>
                    </div>
                </Section>
            ) : null}

            {data.internalNote ? (
                <Section title="Ghi chú nội bộ" titleIcon={<Lock size={14} />} titleColor="#64748b">
                    <div className={styles.detailNoteBox} style={{ borderLeftColor: '#64748b' }}>
                        <span className={styles.detailNoteText}>{data.internalNote}</span>
                    </div>
                </Section>
            ) : null}

            <Section title="Lịch sử">
                <Row label="Ngày mua" value={formatDateTime(data.purchasedAt)} />
                {data.extendedDays > 0 && <Row label="Đã gia hạn" value={`${data.extendedDays} ngày`} />}
            </Section>
        </>
    );
}

function GuideContent({ data, onBack, onClose }) {
    if (!data) return null;
    const snap = data.templateSnapshot || {};
    const description = (snap.description || '').trim();
    const procedureNote = (snap.procedureNote || '').trim();
    const packageName = snap.name || data.package?.name || 'Thẻ dịch vụ';

    return (
        <>
            <div className={styles.headerRow}>
                <button className={styles.iconBtn} onClick={onBack}>
                    <ArrowLeft size={22} />
                </button>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 className={styles.title}>Hướng dẫn sử dụng</h3>
                    <span className={styles.code}>{packageName}</span>
                </div>
                <button className={styles.closeBtn} onClick={onClose}>
                    <X size={22} />
                </button>
            </div>

            <Section title="Mô tả ngắn" titleIcon={<Info size={14} />} titleColor="#0089F6">
                {description ? (
                    <span className={styles.guideText}>{description}</span>
                ) : (
                    <span className={styles.muted}>Chưa có mô tả ngắn</span>
                )}
            </Section>

            <Section title="Quy trình theo buổi" titleIcon={<List size={14} />} titleColor="#16a34a">
                {procedureNote ? (
                    <span className={styles.guideText}>{procedureNote}</span>
                ) : (
                    <span className={styles.muted}>Chưa có quy trình chi tiết</span>
                )}
            </Section>
        </>
    );
}

function Stat({ label, value, highlight }) {
    return (
        <div className={`${styles.stat}${highlight ? ` ${styles.statHighlight}` : ''}`}>
            <span className={styles.statLabel}>{label}</span>
            <span className={styles.statValue} style={highlight ? { color: '#0089F6' } : undefined}>
                {value}
            </span>
        </div>
    );
}

function Section({ title, titleIcon, titleColor, children }) {
    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                {titleIcon ? <span style={{ color: titleColor || '#64748b', display: 'flex' }}>{titleIcon}</span> : null}
                <span className={styles.sectionTitle} style={titleColor ? { color: titleColor } : undefined}>
                    {title}
                </span>
            </div>
            <div className={styles.sectionContent}>{children}</div>
        </div>
    );
}

function Row({ label, value, valueColor, bold }) {
    return (
        <div className={styles.row}>
            <span className={styles.rowLabel}>{label}</span>
            <span
                className={styles.rowValue}
                style={{ ...(valueColor ? { color: valueColor } : {}), ...(bold ? { fontWeight: 700 } : {}) }}
            >
                {value}
            </span>
        </div>
    );
}

function ActionBtn({ icon, label, onClick, color }) {
    return (
        <button className={styles.actionBtn} onClick={onClick} style={{ color }}>
            {icon}
            {label}
        </button>
    );
}
